import { Stack } from '@mui/material';
import { PaymentFrequencyType } from '@schooly/api';
import { FC, PropsWithChildren, useCallback, useState } from 'react';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { toggleMultipleValueArrayProperty } from '../utils';
import { PAYMENT_FREQUENCY_TYPES } from './FrequencySelectMultiple';
import { FrequencyTagSelect, FrequencyTagSelectProps } from './FrequencyTagSelect';

type FrequencyExpandedSelectProps = PropsWithChildren<{
  selectedTypes: PaymentFrequencyType[];
  onApply: (v: PaymentFrequencyType[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;
export const FrequencyExpandedSelect: FC<FrequencyExpandedSelectProps> = ({
  selectedTypes: initialSelectedTypes,
  onApply,
  onClose,
  onClear,
}) => {
  const [selectedTypes, setSelectedTypes] = useState<PaymentFrequencyType[]>(initialSelectedTypes);

  const toggleTypes = useCallback((v: PaymentFrequencyType[]) => {
    setSelectedTypes((types) => toggleMultipleValueArrayProperty(types, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedTypes);
    onClose();
  }, [selectedTypes, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderFrequencyTags({
            types: PAYMENT_FREQUENCY_TYPES,
            tagProps: (type) => ({
              variant: selectedTypes.includes(type) ? 'filled' : undefined,
            }),
            onClick: (type) => toggleTypes([type]),
          })}
        </Stack>
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [handleApply, onClear, toggleTypes, selectedTypes]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedTypes.length > 0}
      renderContent={renderContent}
      onClose={onClose}
      onClear={onClear}
    >
      {renderFrequencyTags({
        types: selectedTypes,
        tagProps: { sx: { maxWidth: 200 }, size: 'small' },
        onDelete: (type) => toggleTypes([type]),
      })}
    </ExpandedSelect>
  );
};

type RenderFrequencyTagsParams = {
  types: PaymentFrequencyType[];
  onDelete?: (v: PaymentFrequencyType) => void;
  onClick?: (v: PaymentFrequencyType) => void;
  tagProps?:
    | Omit<FrequencyTagSelectProps, 'type'>
    | ((v: PaymentFrequencyType) => Omit<FrequencyTagSelectProps, 'type'>);
};

export const renderFrequencyTags = ({
  types,
  onDelete,
  onClick,
  tagProps,
}: RenderFrequencyTagsParams) =>
  types.map((v) => (
    <FrequencyTagSelect
      key={v}
      type={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
